/*
 * Loads the form guard (assets/cdx-mail-validation.js, ~50 KB) only when a visitor starts using a
 * form instead of on every page load. Config: JSON island in snippets/cdx-mail-validation.liquid.
 *
 * - First trusted focus/pointer/key/paste inside a form (that isn't in config.skipForms) loads it.
 * - Pages with a Shopify Forms block (shadow DOM: its submit can't be intercepted here), the
 *   data-eager attribute or the validator's test mode load it when the page is idle.
 * - A submit before the validator is ready is held back and re-sent once it has loaded
 *   (or sent unchanged if loading fails – a form is never blocked by the loader).
 * - Keys/pastes/pointer input seen before loading are recorded on form.__cdxMv and
 *   form.dataset.cdxMvHuman, exactly where the validator keeps them, so its bot checks
 *   (time trap, "no keyboard input") see the whole interaction.
 */
(() => {
  if (window.cdxMailValidationLoader) return;
  window.cdxMailValidationLoader = true;

  const script = document.currentScript;
  const validatorSrc = script?.dataset.validatorSrc;
  if (!validatorSrc) return;

  let config = {};
  try {
    config = JSON.parse(document.getElementById("cdx-mail-validation-config").textContent);
  } catch (error) {
    // keep defaults
  }
  window.CDX_MAIL_VALIDATION = window.CDX_MAIL_VALIDATION || config;
  const skipForms = config.skipForms || "";

  let loading = null;
  let ready = false;
  const load = () => {
    if (!loading) {
      loading = new Promise((resolve) => {
        const validator = document.createElement("script");
        validator.src = validatorSrc;
        validator.onload = () => {
          ready = true;
          resolve(true);
        };
        validator.onerror = () => resolve(false);
        document.head.appendChild(validator);
      });
    }
    return loading;
  };

  const isGuardedForm = (form) => !!form && !(skipForms && form.matches(skipForms));

  // Closest form for the event, also inside open shadow roots (Shopify Forms)
  const formFor = (event) => {
    for (const node of event.composedPath()) {
      if (node.tagName === "FORM") return node;
    }
    return null;
  };

  const onInteraction = (event) => {
    if (ready || !event.isTrusted) return;
    const form = formFor(event);
    if (!isGuardedForm(form)) return;

    if (event.type !== "focusin") {
      const state =
        form.__cdxMv ||
        (form.__cdxMv = { keys: 0, pastes: 0, pointers: 0, started: Date.now(), fields: {} });
      if (event.type === "keydown") state.keys++;
      if (event.type === "paste") state.pastes++;
      if (event.type === "pointerdown") state.pointers++;
      form.dataset.cdxMvHuman = "1";
    }
    load();
  };
  ["focusin", "pointerdown", "keydown", "paste"].forEach((type) =>
    document.addEventListener(type, onInteraction, true)
  );

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (ready || !isGuardedForm(form)) return;
      if (form.__cdxMvLoaderPass) {
        delete form.__cdxMvLoaderPass;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;
      load().then((loaded) => {
        if (!loaded) form.__cdxMvLoaderPass = true;
        if (typeof form.requestSubmit !== "function") {
          form.submit();
        } else if (submitter && submitter.form === form) {
          form.requestSubmit(submitter);
        } else {
          form.requestSubmit();
        }
      });
    },
    true
  );

  const testMode = () => {
    try {
      return /[?&]cdx-mv-test=1/.test(location.search) || sessionStorage.getItem("cdxmv-test") === "1";
    } catch (error) {
      return false;
    }
  };
  const loadWhenIdle = () =>
    "requestIdleCallback" in window ? requestIdleCallback(load, { timeout: 3000 }) : setTimeout(load, 1);
  const onPageLoad = () => {
    // [data-form-root]: server-rendered placeholder of the Shopify Forms block (the embed is created later)
    if (
      script.hasAttribute("data-eager") ||
      testMode() ||
      document.querySelector("shopify-forms-embed, [data-form-root]")
    ) {
      loadWhenIdle();
    }
  };
  if (document.readyState === "complete") onPageLoad();
  else window.addEventListener("load", onPageLoad, { once: true });
})();
